import React, { useEffect, useRef, useState } from 'react';
import './ProteinViewer.css';
import api from '../../../api/axios.js';
import { plddtColorFunc } from '../utils/plddtColor.js';
import { chainColorFunc } from '../utils/chainColor.js';

// Residue names of single-atom ion cofactors that should always be displayed
// (as spheres) regardless of the ligand toggles. Larger het molecules stay
// behind the toggles.
const ION_RESNS = [
  'MG', 'MN', 'ZN', 'CA', 'NA', 'K', 'FE', 'FE2', 'CO', 'NI', 'CU', 'CU1',
  'CD', 'HG', 'CL', 'BR', 'IOD', 'MO', 'W', 'SR', 'BA', 'CS', 'RB', 'LI'
];

const ProteinViewer = ({
  viewerRef,
  surfaceRef,
  setIsSurfaceShown,
  setCurrentStyle,
  enzymData,
  selectedStructure,
  activeLigands,
  hasStructure,
  setHasStructure,
  currentColor,
  currentStyle,
  activeMotifs,
  showMotifSideChains,
  colorByPlddt,
  setChains,
  motifs
}) => {

  const containerRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isStructureRendered, setIsStructureRendered] = useState(false);

  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  // Init viewer
  useEffect(() => {
    const initViewer = async () => {
      try {
        const element = containerRef.current;
        if (!element) return;

        if (!viewerRef.current) {
          const newViewer = window.$3Dmol.createViewer(element, {
            backgroundColor: 'white',
          });
          viewerRef.current = newViewer;
        }
        setIsLoading(false);
      } catch (err) {
        console.error('Error initializing 3Dmol:', err);
        setError(`Initialization error: ${err.message}`);
        setIsLoading(false);
      }
    };
    initViewer();

  
    return () => {
      if (viewerRef.current) {
        try {
          viewerRef.current.clear();
        } catch (cleanupErr) {
          console.warn("Error cleaning up viewer:", cleanupErr);
        }
        viewerRef.current = null;
      }
    };
  }, [viewerRef]);

  // Load struktury a STYLOVÁNÍ LIGANDŮ
  useEffect(() => {
    let canceled = false;
    const element = containerRef.current;
    if (!selectedStructure || !viewerRef.current) {
      setIsStructureRendered(false);
      setIsLoading(false);
      return;
    }

    const waitForNonZeroSize = async () => {
      let tries = 0;
      while (element && (element.clientWidth === 0 || element.clientHeight === 0) && tries < 40) {
        await new Promise(r => setTimeout(r, 50));
        tries++;
      }
    };

    const loadStructure = async () => {
      setIsLoading(true);
      setError(null);
      setIsStructureRendered(false);
      try {
        await waitForNonZeroSize();
        const response = await api.get(selectedStructure.path, { responseType: "text", headers: { Accept: "*/*" } });
        if (!response.data) throw new Error(`Failed to fetch structure`);
        const structureData = response.data;
        const viewer = viewerRef.current;
        if (!viewer) return;

        viewer.clear();
        viewer.addModel(structureData, selectedStructure.format || 'pdb');

        const baseStyles = colorByPlddt
          ? {
              cartoon: { cartoon: { colorfunc: plddtColorFunc } },
              spheres: { sphere: { colorfunc: plddtColorFunc, radius: 1.3 } },
              lines:   { line:   { colorfunc: plddtColorFunc, linewidth: 2 } },
              sticks:  { stick:  { colorfunc: plddtColorFunc, radius: 0.3 } }
            }
          : {
              cartoon: { cartoon: { colorfunc: chainColorFunc } },
              spheres: { sphere: { colorfunc: chainColorFunc, radius: 1.3 } },
              lines:   { line:   { colorfunc: chainColorFunc, linewidth: 2 } },
              sticks:  { stick:  { colorfunc: chainColorFunc, radius: 0.3 } }
            };
        const style = baseStyles[currentStyle] || baseStyles.cartoon;
        viewer.setStyle({}, style);

        if (currentStyle === "cartoon" && activeMotifs && activeMotifs.length > 0 && motifs) {
          activeMotifs.forEach(motifName => {
            const motif = motifs.find(m => m.name === motifName);
            if (motif && motif.ranges) {
              motif.ranges.forEach(([start, end]) => {
                const selection = { resi: Array.from({length: end - start + 1}, (_, i) => start + i) };

                // setStyle (replace), NOT addStyle: the base cartoon style carries a
                // colorfunc (chain / pLDDT), and colorfunc wins over a plain `color`
                // when both are present on the same atom. Merging would leave the
                // colorfunc in place, so the motif color would never show. Replacing
                // the cartoon spec drops the colorfunc for these residues.
                viewer.setStyle(selection, { cartoon: { color: motif.color } });
                
                
                if (showMotifSideChains) {
                  
                  viewer.addStyle(
                    selection,
                    { stick: { colorscheme: 'greenCarbon', radius: 0.2 } }
                  );
                  
                  
                  viewer.addStyle(
                    {
                      ...selection,
                      atom: ['N', 'CA', 'C', 'O']  
                    },
                    {} 
                  );
                }
              });
            }
          });
        }

        if (activeLigands && activeLigands.length > 0) {
            viewer.addStyle({hetflag: true}, {stick: {colorscheme: 'greenCarbon', radius: 0.25}});
        }

        // Single-atom ion cofactors (Mg, Mn, …) are always shown: a lone ion has
        // no bonds, so it is invisible in cartoon/stick/line styles. Added last so
        // it wins over any base/ligand style on those atoms.
        viewer.addStyle({ resn: ION_RESNS }, { sphere: { radius: 0.8, colorscheme: 'default' } });

        // Report the protein chains present so the settings panel can render a
        // matching chain-color legend.
        if (setChains) {
          try {
            const model = viewer.getModel();
            const atoms = model ? model.selectedAtoms({}) : [];
            const chainSet = [...new Set(atoms.filter(a => !a.hetflag && a.chain).map(a => a.chain))].sort();
            if (!canceled) setChains(chainSet);
          } catch (e) {
            console.warn('Could not extract chains:', e);
          }
        }

        viewer.zoomTo();
        //viewer.zoom(1.3);

        if (element && element.clientWidth > 0 && element.clientHeight > 0) {
          viewer.resize();
          if (!isSafari) {
            viewer.render();
          } else {
            setTimeout(() => {
              try {
                viewer.resize();
                viewer.render();
              } catch (e) {
                console.warn("Safari render skipped:", e);
              }
            }, 50);
          }
        }

        if (!canceled) setIsStructureRendered(true);
      } catch (err) {
        console.error('Error loading structure:', err);
        if (!canceled) {
          // Structure files are no longer validated up front, so a missing file
          // first shows up here — give it a readable message rather than the
          // raw axios "Request failed with status code 404".
          const missing = err?.response?.status === 404;
          setError(missing
            ? `Structure file not available${selectedStructure.file_name ? ` (${selectedStructure.file_name})` : ''}.`
            : `Loading error: ${err.message}`);
        }
      } finally {
        if (!canceled) setIsLoading(false);
      }
    };

    loadStructure();

    return () => {
      canceled = true;
    };
  }, [selectedStructure, viewerRef, currentStyle, currentColor, activeLigands, activeMotifs, showMotifSideChains, colorByPlddt, motifs]); 

  return (
    <div className="viewer-container">
      <div
        ref={containerRef}
        className={`viewer-port ${!hasStructure ? 'no-structure' : ''}`}
      />
      {isLoading && (
        <div className="viewer-overlay">
          <div className="spinner"></div>
          <span>Loading structure...</span>
        </div>
      )}
      {error && (
        <div className="viewer-overlay error-overlay">
          <span>Error: {error}</span>
        </div>
      )}
      {!isLoading && !error && !hasStructure && (
        <div className="viewer-overlay">
          <span>Structure not available.</span>
        </div>
      )}
    </div>
  );
};

export default ProteinViewer;
